/**
 * PHASE SPLIT round (docs/MODELER.md): resolves a closed-boundary element's
 * `phaseSplits` param into leaf PHASE REGIONS — the Option-B answer to partial
 * demo/new work on a floor or ceiling without modeling the element twice (roofs
 * are a later round; the per-face plane-solve interacts with framing/tiles/join
 * and needs its own pass).
 *
 * Reuses two already-tested primitives from the CAD detail-drafting tools
 * (commands/draftingOps.ts) instead of inventing new polygon math:
 * `projectToPathParam` snaps a point onto the current boundary, `splitPathSpans`
 * splits a closed path at two params into the two open boundary ARCS between
 * them. Closing an arc's own last point back to its first is exactly the straight
 * chord the split drew — no separate boolean-clip step, and every region is
 * provably coplanar/coincident with its siblings because it's a slice of the
 * SAME boundary, never a second modeled object.
 */
import type { Path2D, PhaseId, PhaseSplitEntry, Pt2 } from './types'
import { newNodeId } from './ids'
import { flattenPathPts } from './arc'
import { projectToPathParam, splitPathSpans } from './commands/draftingOps'

export interface PhaseRegion {
  /** the parent element's own tags for the untouched base piece; a split's own id
   *  for the piece it carved off */
  id: string
  /** closed polygon, world XY feet — fill area and 3D extrusion both use this whole */
  ring: Pt2[]
  /**
   * false when this region came from a split: `ring`'s own closing edge (last
   * point back to first) IS that split's chord, and 2D stroking should draw
   * `ring` as an OPEN polyline (the chord is drawn separately, only when the
   * view's graphics mode wants the seam visible — see `PhaseSplitResolution.seams`).
   * true for an untouched region (no split ever divided it) — draws as a normal
   * closed outline, nothing to exclude.
   */
  strokeClosed: boolean
  phaseCreated?: PhaseId
  phaseDemolished?: PhaseId
}

export interface PhaseSplitResolution {
  regions: PhaseRegion[]
  /** one chord per split actually applied, for the 2D seam line — drawn once,
   *  never per-region (both regions bordering it already reuse these same points).
   *  `splitId` matches the `PhaseRegion.id` of the piece that split carved off, so a
   *  consumer can attach each seam to exactly the one solid that should carry it. */
  seams: { splitId: string; a: Pt2; b: Pt2 }[]
}

/** ray-casting point-in-polygon, world XY feet — local to avoid a bim-geometry dependency */
export function pointInRing(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const crosses = yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi
    if (crosses) inside = !inside
  }
  return inside
}

function ringToClosedPath(ring: Pt2[]): Path2D {
  return { closed: true, nodes: ring.map((pt) => ({ id: newNodeId(), pt })) }
}

const BASE_REGION_ID = 'base'
const DEGENERATE_EPS_FT = 1e-3
/** how close `a`/`b` must land to a region's own boundary to count as "on it" —
 *  generous enough for click/snap slop, tight enough to never straddle two
 *  regions that are themselves only a foot or so apart */
const ON_BOUNDARY_EPS_FT = 0.25

function dist(a: Pt2, b: Pt2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1])
}

/**
 * Resolve a closed boundary ring + this element's ordered `phaseSplits` into leaf
 * regions. Splits apply IN ORDER, each against whichever CURRENT region contains
 * both its `a` and `b` points (point-in-ring, not edge/param addressing) — a split
 * left orphaned by a later boundary edit (its points no longer land inside any
 * remaining piece) is silently skipped, never thrown, the same "degrade instead of
 * error" convention the rest of this pipeline follows.
 */
export function resolvePhaseRegions(
  boundaryRing: Pt2[],
  splits: PhaseSplitEntry[] | null | undefined,
  base: { phaseCreated?: PhaseId; phaseDemolished?: PhaseId },
): PhaseSplitResolution {
  let regions: PhaseRegion[] = [{
    id: BASE_REGION_ID,
    ring: boundaryRing,
    strokeClosed: true,
    phaseCreated: base.phaseCreated,
    phaseDemolished: base.phaseDemolished,
  }]
  const seams: { splitId: string; a: Pt2; b: Pt2 }[] = []
  for (const split of splits ?? []) {
    // `a`/`b` are near-boundary BY DESIGN (they anchor the cut to the edge), so
    // "which region owns this split" is "which region's boundary do both points
    // actually land near" — a plain inside/outside test is the wrong question
    // and unreliable right at the edge besides (the classic ray-cast footgun).
    let idx = -1
    let cutA = null as ReturnType<typeof projectToPathParam> | null
    let cutB = null as ReturnType<typeof projectToPathParam> | null
    for (let i = 0; i < regions.length; i++) {
      const path = ringToClosedPath(regions[i].ring)
      const pa = projectToPathParam(path, split.a)
      const pb = projectToPathParam(path, split.b)
      if (dist(split.a, pa.pt) < ON_BOUNDARY_EPS_FT && dist(split.b, pb.pt) < ON_BOUNDARY_EPS_FT) {
        idx = i; cutA = pa; cutB = pb; break
      }
    }
    if (idx < 0 || !cutA || !cutB) continue
    const target = regions[idx]
    if (dist(cutA.pt, cutB.pt) < DEGENERATE_EPS_FT) continue
    const spans = splitPathSpans(ringToClosedPath(target.ring), [cutA, cutB])
    if (spans.length !== 2) continue
    const ring0 = flattenPathPts(spans[0])
    const ring1 = flattenPathPts(spans[1])
    if (ring0.length < 3 || ring1.length < 3) continue
    // `tagSide` — never one of the cut's own boundary points — unambiguously picks
    // which arc gets this split's tags; the other arc keeps the target's own.
    const taggedRing = pointInRing(split.tagSide, ring0) ? ring0 : ring1
    const otherRing = taggedRing === ring0 ? ring1 : ring0
    seams.push({ splitId: split.id, a: cutA.pt, b: cutB.pt })
    regions.splice(idx, 1,
      {
        id: target.id, ring: otherRing, strokeClosed: false,
        phaseCreated: target.phaseCreated, phaseDemolished: target.phaseDemolished,
      },
      {
        id: split.id, ring: taggedRing, strokeClosed: false,
        phaseCreated: split.phaseCreated ?? target.phaseCreated,
        phaseDemolished: split.phaseDemolished ?? target.phaseDemolished,
      },
    )
  }
  return { regions, seams }
}
